import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TourService } from 'ngx-ui-tour-md-menu';
import { tap } from 'rxjs/operators';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UtilsService } from '../../../shared/utils.service';
import { CustomDataSource } from '../../../utils/custom-data-source';
import { SitesFilterComponent } from '../../sites-filter/sites-filter.component';
import { ChartsUtilsService } from '../../../charts/charts-utils.service';
import { SitesFilter } from '../../sites-filter/sites-filter.interface';
import { saveAs } from 'file-saver';
import { RBACService } from '../../../shared/rbac.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-sites',
  templateUrl: './sites.component.html',
  styleUrls: ['./sites.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesComponent implements OnInit, AfterViewInit {
  // Columns to display in the list.
  displayedColumns: string[] = [
    'select',
    'id',
    'name',
    'location',
    'site_type',
    'site_status',
    'site_risk_assessment',
    'geofence',
    'date_created',
    'actions'
  ];

  dataSource: SitesDataSource = new SitesDataSource(this.app, this.api);

  // Permission flags resolved ONCE in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;
  canCreateMessages: boolean = false;
  canViewTags: boolean = false;

  // Paginator and List Sorter References.
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public tour: TourService,
    public utils: UtilsService,
    public chartsUtils: ChartsUtilsService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Resolve the permission flags first so they are always set, even when the early return below fires.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES,
        RBACService.RBAC_A.A_CREATE
      )
    ]);
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES,
        RBACService.RBAC_A.A_VIEW
      )
    ]);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES,
        RBACService.RBAC_A.A_EDIT
      )
    ]);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES,
        RBACService.RBAC_A.A_DELETE
      )
    ]);
    this.canCreateMessages = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_MESSAGES,
        RBACService.RBAC_A.A_CREATE
      )
    ]);
    this.canViewTags = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_ACCOUNT_TAGS,
        RBACService.RBAC_A.A_VIEW
      )
    ]);

    if (!this.canCreate && !this.canView) {
      this.rbac.showGenericAccessDeniedMessage();
      this.router.navigate(['/dashboard']);
      return;
    }

    if (this.canView) {
      this.dataSource.sites_export_term = this.utils.getLangTerm(
        'sites.plural',
        'Sites'
      );

      // Load sites data from the API.
      this.dataSource.getData();
    }
  }

  ngAfterViewInit(): void {
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting.
    this.sort.sortChange.subscribe(
      (): void => {
        this.paginator.pageIndex = 0;
      }
    );

    const _tap = tap(
      (): void => {
        this.dataSource.limit = this.paginator.pageSize;
        this.dataSource.offset = this.paginator.pageIndex;
        this.dataSource.sort_order = this.sort.direction;

        this.dataSource.sort_by = this.sort.active;

        // Reload the list.
        this.dataSource.getData();
      }
    );

    // Subscribe to the paginator and list sorting tap events.
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Filter the list of sites by status.
   * @param status
   */
  onSiteStatusFilter(status: string): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ])
    ) {
      return;
    }

    this.dataSource.status = status;
    this.dataSource.getData(true);
  }

  /**
   * Filter the list of sites by type.
   * @param type
   */
  onSiteTypeFilter(type: string): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ])
    ) {
      return;
    }

    this.dataSource.type = type;
    this.dataSource.getData(true);
  }

  /**
   * Remove a single site.
   * @param id
   */
  onRemove(id: number): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showModal(
      'Remove ' + this.utils.getLangTerm('sites.singular', 'Site'),
      'Are you sure you want to remove this ' +
        this.utils.getLangTerm('sites.singular', 'Site').toLowerCase() +
        '?',
      (): void => {
        this.api
          .makeRequest('delete', 'v1/sites/' + id)
          .then(
            (): void => {
              this.remove();
            }
          )
          .catch(
            (err: any): void => {
              this.utils.showModal('Error', err.error.message);
            }
          );
      }
    );
  }

  /**
   * Remove multiple selected sites.
   */
  onRemoveSelected(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showModal(
      'Remove ' + this.utils.getLangTerm('sites.plural', 'Sites'),
      'Are you sure you want to remove the selected ' +
        this.utils.getLangTerm('sites.plural', 'Sites').toLowerCase() +
        '?',
      (): void => {
        this.api
          .makeRequest(
            'delete',
            'v1/sites/' + this.dataSource.selection.selected.join(',')
          )
          .then(
            (): void => {
              this.remove();
            }
          )
          .catch(
            (err: any): void => {
              this.utils.showModal('Error', err.error.message);
            }
          );
      }
    );
  }

  /**
   * Removed site(s) toast feedback.
   */
  private remove(): void {
    this.utils.showToast(
      'The selected ' +
        this.utils.getLangTerm('sites.plural', 'Sites').toLowerCase() +
        ' was successfully deleted'
    );
    this.dataSource.selection.clear();
    this.dataSource.getData();
  }

  /**
   * Clone a site.
   * @param id
   */
  onCloneSite(id: number): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showModal(
      'Cloning ' + this.utils.getLangTerm('sites.singular', 'Site'),
      'You are about to clone a ' +
        this.utils.getLangTerm('sites.singular', 'Site').toLowerCase() +
        '. Cloning a ' +
        this.utils.getLangTerm('sites.singular', 'Site').toLowerCase() +
        ' will also clone linked ' +
        this.utils.getLangTerm('contractors.plural', 'Contractors') +
        ', managers, documents, hazards & risks, ' +
        this.utils
          .getLangTerm('hazardous-substances.plural', 'Hazardous Substances')
          .toLowerCase() +
        ', emergency response plans and ' +
        this.utils.getLangTerm('ta-jsa-swms.plural', 'TA/JSA/SWMS') +
        ' records. <br><br>Are you sure you want to continue?',
      (): void => {
        this.api.makeRequest('post', `v1/sites/${id}/clone`).then(
          (response: any): void => {
            this.utils.showToast(response.message);
            this.dataSource.getData();
          }
        );
      }
    );
  }

  /**
   * Compose a new message with the site pre-selected.
   */
  composeNewMessage(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    if (this.dataSource.selection.selected.length == 0) {
      this.utils.showToast(
        'You need to select some ' +
          this.utils.getLangTerm('sites.plural', 'Sites').toLowerCase() +
          ' first.'
      );
      return;
    }

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectSiteIds: this.dataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then(
        (response: any): void => {
          if (response) {
            this.utils.showToast(
              'You can view the message stats in the messages feature.'
            );
          }
        }
      );
  }

  /**
   * Filters the output tags based on the provided tag IDs if the user has access permissions.
   *
   * @param {number[]} tag_ids - An array of tag IDs to apply as a filter.
   * @return {void} This method does not return a value. Executes filtering logic.
   */
  getOutputTagsFilter(tag_ids: number[]): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_TAGS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      return;
    }

    this.dataSource.tag_ids = tag_ids;
    this.dataSource.getData(true);
  }

  /**
   * Filter the list of sites.
   */
  onFilter(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        false
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showComponentDialog(
      SitesFilterComponent,
      {
        site_status: this.dataSource.status,
        site_type: this.dataSource.type,
        date_range: this.dataSource.date_filter_range
      } as SitesFilter,
      {
        width: '1024px'
      },
      (results: SitesFilter): void => {
        if (typeof results != 'undefined') {
          this.dataSource.status =
            typeof results.site_status != 'undefined'
              ? results.site_status
              : this.dataSource.status;
          this.dataSource.type =
            typeof results.site_type != 'undefined'
              ? results.site_type
              : this.dataSource.type;
          this.dataSource.date_filter_range = results.date_range
            ? results.date_range
            : this.dataSource.date_filter_range;
          this.dataSource.getData();
        }
      }
    );
  }

  protected readonly RBACService = RBACService;
}

/**
 * The data source for the main sites list.
 */
export class SitesDataSource extends CustomDataSource {
  sort_by: string = 'name';
  sort_order: string = 'asc';

  status: string = '';
  type: string = '';
  tag_ids: number[] = [];
  date_filter_range: Date[] = [];

  sites_export_term: string = 'Sites';

  getData(resetOffset: boolean = false): void {
    this.getDataFromLaravelAPI(
      'sites',
      resetOffset,
      {},
      {
        status: this.status ? this.status : '',
        type: this.type ? this.type : '',
        tag_ids: this.tag_ids.length ? this.tag_ids.join(',') : '',
        date_filter_range:
          this.date_filter_range && this.date_filter_range.length > 1
            ? [
                this.date_filter_range[0].getTime() / 1000,
                this.date_filter_range[1].getTime() / 1000
              ].join(',')
            : ''
      }
    );
  }

  onExport(type: string = 'pdf', id?: number): void {
    let ids: number[] = [];
    if (id) {
      ids.push(id);
    } else {
      ids.push(...this.selection.selected);
    }

    this.api
      .makeDownloadRequest(
        `v1/sites/export/${type}` + (ids.length > 0 ? '/' + ids.join(',') : ''),
        {},
        {
          status: this.status ? this.status : '',
          type: this.type ? this.type : '',
          tag_ids: this.tag_ids.length ? this.tag_ids.join(',') : '',
          date_filter_range:
            this.date_filter_range && this.date_filter_range.length > 1
              ? [
                  this.date_filter_range[0].getTime() / 1000,
                  this.date_filter_range[1].getTime() / 1000
                ].join(',')
              : ''
        }
      )
      .then((response) => {
        saveAs(
          response,
          `${this.app.account.name} - ${this.sites_export_term}.${type}`
        );
      });
  }
}
